// src/App.js — Routes: CRM Dashboard (owners) + Client Portal (clients) + Public

import { BrowserRouter as Router, Routes, Route, Navigate, Link } from "react-router-dom";
import PrivateRoute from "./components/shared/PrivateRoute";
import RoleGate from "./components/shared/RoleGate";

import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
// REMOVED-CRM-ROUTES (2026-08-23) — the legacy CRM/business platform is
// dormant: its routes and imports are commented out below (never deleted;
// restore this whole sentinel family together). Backend routes stay deployed.
// import CRMDashboard from "./pages/CRMDashboard";
import LoggedOutPage from "./pages/LoggedOutPage";
// import CreateBusinessPage from "./pages/CreateBusinessPage";
import ClaimAccountPage from "./pages/ClaimAccountPage";
// import AccountNotificationsPage from "./pages/AccountNotificationsPage";
import AccountSecurityPage from "./pages/AccountSecurityPage";
// import AdminMatchmaking from "./pages/AdminMatchmaking";
// import AdminMatchStats from "./pages/AdminMatchStats";
// import AdminPlacements from "./pages/AdminPlacements";
// import AdminAssignments from "./pages/AdminAssignments";
import AdminOfficials from "./pages/AdminOfficials";
// import FindingAcademyPage from "./pages/FindingAcademyPage";
// END-REMOVED-CRM-ROUTES
import RetiredAccountPage from "./pages/RetiredAccountPage";
import OfficialDashboard from "./pages/OfficialDashboard";

// Coach portal (Players/Teams + Games — scorekeeping is iOS-only, and
// league-scheduled now, not coach-owned)
import CoachLayout from "./pages/coach/CoachLayout";
import CoachPlayersPage from "./pages/coach/CoachPlayersPage";
import CoachPlayerStatsPage from "./pages/coach/CoachPlayerStatsPage";
import CoachGamesPage from "./pages/coach/CoachGamesPage";

// League portal (Teams + Schedule + Officials)
import LeagueLayout from "./pages/league/LeagueLayout";
import LeagueTeamsPage from "./pages/league/LeagueTeamsPage";
import LeagueSchedulePage from "./pages/league/LeagueSchedulePage";
import LeagueOfficialsPage from "./pages/league/LeagueOfficialsPage";
import LeagueCoachesPage from "./pages/league/LeagueCoachesPage";
import LeagueTournamentPage from "./pages/league/LeagueTournamentPage";

// REMOVED-CRM-ROUTES (2026-08-23) — client portal (see sentinel above)
/*
import ClientLayout from "./pages/client/ClientLayout";
import ClientHomePage from "./pages/client/ClientHomePage";
import ClientSearchPage from "./pages/client/ClientSearchPage";
import ClientBusinessPage from "./pages/client/ClientBusinessPage";
import ClientSubscriptionsPage from "./pages/client/ClientSubscriptionsPage";
import ClientSubscriptionDetailPage from "./pages/client/ClientSubscriptionDetailPage";
import ClientMessagesPage from "./pages/client/ClientMessagesPage";
import ClientMatchesPage from "./pages/client/ClientMatchesPage";
import ClientChildStatsPage from "./pages/client/ClientChildStatsPage";
import ClientChildRatingDetailPage from "./pages/client/ClientChildRatingDetailPage";
import ClientProfilePage from "./pages/client/ClientProfilePage";
*/
// END-REMOVED-CRM-ROUTES

// Public (no auth) pages
// REMOVED-CRM-ROUTES (2026-08-23) — public registration + Stripe Connect
// return pages (see sentinel above)
// import RegistrationPage from "./pages/public/RegistrationPage";
import TermsPage from "./pages/public/TermsPage";
import PrivacyPage from "./pages/public/PrivacyPage";
// import OnboardingComplete from "./pages/public/OnboardingComplete";
// import OnboardingRefresh from "./pages/public/OnboardingRefresh";
// END-REMOVED-CRM-ROUTES
import StaffInviteClaimPage from "./pages/public/StaffInviteClaimPage";

function NotFoundPage() {
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#000", color: "#fff", fontFamily: "var(--font-primary, sans-serif)" }}>
      <div style={{ textAlign: "center" }}>
        <h1 style={{ fontSize: 72, fontWeight: 900, margin: 0 }}>404</h1>
        <p style={{ color: "rgba(255,255,255,0.5)", marginBottom: 24 }}>Page not found</p>
        <Link to="/" style={{ color: "#fff", textDecoration: "underline" }}>Go home</Link>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/logged-out" element={<LoggedOutPage />} />
        <Route path="/claim" element={<ClaimAccountPage />} />

        {/* REMOVED-CRM-ROUTES (2026-08-23) — public registration link (NO AUTH)
        <Route path="/r/:slug/:location?" element={<RegistrationPage />} />
        END-REMOVED-CRM-ROUTES */}

        {/* League/coach/official SMS-invite claim link (NO AUTH) —
            https://app.polyp.me/join/{inviteId}.{secret}. Also the iOS
            universal-link target (applinks:app.polyp.me, AASA restricts the
            app-link path pattern to /join/*). */}
        <Route path="/join/:token" element={<StaffInviteClaimPage />} />

        {/* Legal pages */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* REMOVED-CRM-ROUTES (2026-08-23) — Stripe Connect return URLs. The
            paths are HARDCODED in polyp-payments-create-onboarding-link;
            restore these together with that Lambda's flow.
        <Route path="/onboarding/complete" element={<OnboardingComplete />} />
        <Route path="/onboarding/refresh" element={<OnboardingRefresh />} />
        END-REMOVED-CRM-ROUTES */}

        {/* Protected Routes (requires auth via PrivateRoute) */}
        <Route path="/" element={<PrivateRoute />}>
          {/* Default — RoleGate decides whether to send to /portal or /app */}
          <Route index element={<RoleGate />} />

          {/* REMOVED-CRM-ROUTES (2026-08-23) — owner CRM. Legacy owners now
              land on /retired (see RoleGate).
          <Route element={<RoleGate require="owner" />}>
            <Route path="portal" element={<CRMDashboard />} />
          </Route>
          END-REMOVED-CRM-ROUTES */}

          {/* Legacy CRM accounts (client parents + business owners) land here
              — see RoleGate's retired-account redirects. */}
          <Route path="retired" element={<RetiredAccountPage />} />

          {/* Platform-admin routes — gated to allowlisted operators only */}
          <Route element={<RoleGate require="admin" />}>
            {/* REMOVED-CRM-ROUTES (2026-08-23) — academy-matchmaking + lead
                placement consoles (CRM). /admin/officials stays: it still
                gates officialStatus approvals for the live Official tier.
            <Route path="admin/matchmaking" element={<AdminMatchmaking />} />
            <Route path="admin/match-stats" element={<AdminMatchStats />} />
            <Route path="admin/placements" element={<AdminPlacements />} />
            <Route path="admin/assignments" element={<AdminAssignments />} />
            END-REMOVED-CRM-ROUTES */}
            <Route path="admin/officials" element={<AdminOfficials />} />
          </Route>

          {/* REMOVED-CRM-ROUTES (2026-08-23) — Instagram lead-ad intake;
              awaiting-placement accounts now land on /retired.
          <Route element={<RoleGate require="placement" />}>
            <Route path="finding-academy" element={<FindingAcademyPage />} />
          </Route>
          END-REMOVED-CRM-ROUTES */}

          {/* Game-official accounts — claim games + enter stats. Both pending
              and approved officials land here; the page gates on
              officialStatus. */}
          <Route element={<RoleGate require="official" />}>
            <Route path="official" element={<OfficialDashboard />} />
          </Route>

          {/* Coach accounts — roster CRUD + read-only games (the league
              schedules, an official tracks). Scorekeeping (live stat
              tracking) is iOS-only; web is Players + Games. */}
          <Route element={<RoleGate require="coach" />}>
            <Route path="coach" element={<CoachLayout />}>
              <Route index element={<CoachPlayersPage />} />
              <Route path="teams/:teamId" element={<CoachPlayersPage />} />
              <Route path="players/:playerId" element={<CoachPlayerStatsPage />} />
              <Route path="games" element={<CoachGamesPage />} />
            </Route>
          </Route>

          {/* League accounts — create teams, schedule games, and bring in
              coaches/officials three ways: an SMS StaffInvites claim link
              (LeagueOfficialsPage, LeagueCoachesPage, LeagueTeamsPage's
              AddTeamModal), or a #hashtag invitation to an existing
              coach-owned team (LeagueTeamsPage's AffiliateTeamModal). */}
          <Route element={<RoleGate require="league" />}>
            <Route path="league" element={<LeagueLayout />}>
              <Route index element={<LeagueTeamsPage />} />
              <Route path="schedule" element={<LeagueSchedulePage />} />
              <Route path="tournaments/:tournamentId" element={<LeagueTournamentPage />} />
              <Route path="coaches" element={<LeagueCoachesPage />} />
              <Route path="officials" element={<LeagueOfficialsPage />} />
            </Route>
          </Route>

          {/* REMOVED-CRM-ROUTES (2026-08-23) — business creation wizard +
              account notifications (business invites), both CRM-only.
          <Route path="create-business" element={<CreateBusinessPage />} />
          <Route path="notifications" element={<AccountNotificationsPage />} />
          END-REMOVED-CRM-ROUTES */}

          {/* Sign-in methods — link Google/Apple/Phone/Password to one Firebase UID */}
          <Route path="security" element={<AccountSecurityPage />} />

          {/* REMOVED-CRM-ROUTES (2026-08-23) — the entire client portal.
              Legacy clients now land on /retired (see RoleGate).
          <Route element={<RoleGate require="client" />}>
            <Route path="app" element={<ClientLayout />}>
              <Route index element={<ClientHomePage />} />
              <Route path="search" element={<ClientSearchPage />} />
              <Route path="business/:businessId" element={<ClientBusinessPage />} />
              <Route path="subscriptions" element={<ClientSubscriptionsPage />} />
              <Route path="subscriptions/:businessId" element={<ClientSubscriptionDetailPage />} />
              <Route path="messages" element={<ClientMessagesPage />} />
              <Route path="messages/dm/:conversationId" element={<ClientMessagesPage />} />
              <Route path="messages/group/:businessId/:groupId" element={<ClientMessagesPage />} />
              <Route path="matches" element={<ClientMatchesPage />} />
              <Route path="matches/:childId" element={<ClientChildStatsPage />} />
              <Route path="matches/:childId/rating" element={<ClientChildRatingDetailPage />} />
              <Route path="notifications" element={<AccountNotificationsPage embedded />} />
              <Route path="profile" element={<ClientProfilePage />} />
            </Route>
          </Route>
          END-REMOVED-CRM-ROUTES */}
        </Route>

        {/* 404 — unknown routes */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}
